// A time the office types into an offer, rather than picks from the list.
//
// The list steps every half hour; sometimes the time agreed with a patient is
// 10:15. The server judges a typed time by the same rule the list is built
// from (GET /scheduling/check-time), so it is as safe to offer and to hold as
// a listed one. This module is the panel's half: the slot a free time
// becomes, and the sentence staff read when it is not free.

interface Mapping {
  service?: { id?: string; name?: string } | null;
  specialist?: { id?: string; name?: string } | null;
}

/** One sentence saying why, so what to try next is obvious. `label` is the time as typed, e.g. "2:15 PM". */
export function typedTimeRefusal(label: string, reason: string | null | undefined, providerName: string): string {
  const whose = providerName ? `${providerName}'s` : "the provider's";
  switch (reason) {
    case 'overlaps an existing appointment':
      return `${label} is not free — it overlaps an existing appointment.`;
    case 'held for a patient':
      return `${label} is not free — it is held for another patient.`;
    case 'outside working hours':
      return `${label} is outside ${whose} working hours.`;
    case 'the appointment would run past the end of the working period':
      return `${label} is too late — the appointment would run past the end of ${whose} working hours.`;
    case 'this time has already passed':
      return `${label} has already passed.`;
    case 'same-day bookings are switched off':
      return 'Same-day bookings are switched off, so a time today cannot be offered.';
    default:
      return `${label} is not free.`;
  }
}

/** The offer entry for a typed time that is free: a slot like a listed one, with its own service, provider and length. */
export function typedSlot(date: string, time: string, duration: number, mapping: Mapping | null) {
  return {
    key: `${date}|${time}|${mapping?.specialist?.id || ''}|typed`,
    slot: {
      date,
      time,
      duration,
      available: true,
      specialist: mapping?.specialist?.name || '',
      specialistId: mapping?.specialist?.id || '',
      serviceType: mapping?.service?.name || '',
      serviceId: mapping?.service?.id || '',
    },
  };
}

const clock = (time?: string | null): string => String(time || '').slice(0, 5);

/** Whether this day, time and provider is in the offer already — listed or typed. */
export function alreadyOffered(
  offer: Array<{ slot: { date?: string; time?: string; specialistId?: string } }>,
  candidate: { date?: string; time?: string; specialistId?: string },
): boolean {
  return offer.some((item) =>
    item.slot.date === candidate.date
    && clock(item.slot.time) === clock(candidate.time)
    && (item.slot.specialistId || '') === (candidate.specialistId || ''));
}

/**
 * The offer in the order the times happen. A time added later may be earlier
 * in the day; the patient's text numbers the times and the panel lists them,
 * so both follow the clock, not the order they were clicked. Stable: two
 * entries at the same moment (a lab draw and a visit) keep their order.
 */
export function inTimeOrder<T extends { slot: { date?: string; time?: string } }>(offer: T[]): T[] {
  const when = (item: T) => `${item.slot.date || ''} ${clock(item.slot.time)}`;
  return offer
    .map((item, index) => ({ item, index }))
    .sort((a, b) => (when(a.item) < when(b.item) ? -1 : when(a.item) > when(b.item) ? 1 : a.index - b.index))
    .map(({ item }) => item);
}
